import { useMutation, useQueryClient } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { DELETE_URL } from '@utils/api/endpoints.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

type DeleteItemParams = {
  itemId: string;
  force?: boolean;
};

export const useDeleteItem = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const deleteItem = async ({ itemId, force = false }: DeleteItemParams) => {
    const url = force
      ? `${DELETE_URL(itemId).deleteQueueItem}?force=true`
      : DELETE_URL(itemId).deleteQueueItem;
    const response = await Axios.delete(url);
    return response.data;
  };

  return useMutation<void, AxiosError<BaseError>, DeleteItemParams>(
    deleteItem,
    {
      onSuccess: () => {
        toast.success(t('item-deleted-successfully'));
        queryClient.invalidateQueries(['priorityQueue']);
        queryClient.invalidateQueries(['queueSummary']);
      },
      onError: (error: AxiosError<BaseError>) => {
        const errorMessage =
          error.response?.data?.error_description ||
          error.response?.data?.detail ||
          error.response?.data?.error ||
          error.response?.data?.message ||
          t('failed-to-delete-item');
        toast.error(errorMessage);
      }
    }
  );
};
